import { useEffect, useState } from 'react';
import { getMobileAppPlatform } from 'utils/isMobileAppRuntime';
import {
  ensureOfflineAssetUrls,
  resolveOfflineAssetKeySync,
  logOfflineDiagnosticEvent,
} from './assets';

// QuickStart/ParentVideos are deferred in assets.js. Introduction is only
// requested for the generated Custom Level intro, never from Placement.
// Activity-local video remains owned by its Activity TAR, not this service.
export const isOnDemandSharedVideo = (url = '') => {
  const text = String(url || '').split('?')[0].split('#')[0];
  return /\/Files\/animations\/(?:QuickStart|ParentVideos|Introduction)\/.+\.(?:mp4|webm|mov|m4v)$/i.test(text);
};

// A single video is downloaded only when its player is opened. The native
// offline storage plugin persists the bytes and the existing asset.meta.v1
// registry survives app restarts. Never expose the remote URL as a fallback
// in a native runtime: doing so could bypass the cache on an intermittent link.
export function useOnDemandVideo(url, reason = 'video') {
  const platform = getMobileAppPlatform();
  const native = (platform === 'ios' || platform === 'android' || platform === 'native')
    && (Boolean(window.Capacitor?.isNativePlatform?.())
      || ['ios', 'android'].includes(String(window.Capacitor?.getPlatform?.() || '').toLowerCase()));
  const requestedUrl = String(url || '');
  const shouldCache = native && isOnDemandSharedVideo(requestedUrl);
  const [result, setResult] = useState({ source: '', url: '', status: 'loading' });

  useEffect(() => {
    if (!requestedUrl || !shouldCache) return undefined;
    let mounted = true;
    setResult({ source: requestedUrl, url: '', status: 'loading' });

    (async () => {
      try {
        const response = await ensureOfflineAssetUrls([requestedUrl], { reason });
        // ensureOfflineAssetUrls reads the registry and tests connectivity;
        // a successful result is not enough until a readable local URI exists.
        const localUrl = resolveOfflineAssetKeySync(requestedUrl);
        if (!mounted) return;
        if (localUrl) {
          setResult({ source: requestedUrl, url: localUrl, status: 'ready' });
          await logOfflineDiagnosticEvent('video-on-demand-ready', {
            reason, sourceUrl: requestedUrl, fromCache: response.downloadedCount === 0,
          });
        } else {
          const offline = response?.status === 'offline-missing';
          setResult({ source: requestedUrl, url: '', status: offline ? 'offline-missing' : 'error' });
          await logOfflineDiagnosticEvent('video-on-demand-unavailable', {
            reason, sourceUrl: requestedUrl, status: response?.status || 'no-local-uri',
          });
        }
      } catch (error) {
        if (!mounted) return;
        setResult({ source: requestedUrl, url: '', status: 'error' });
        await logOfflineDiagnosticEvent('video-on-demand-error', {
          reason, sourceUrl: requestedUrl, error: String(error?.message || error),
        });
      }
    })();
    return () => { mounted = false; };
  }, [requestedUrl, reason, shouldCache]);

  if (!shouldCache) return { status: 'ready', url: requestedUrl };
  if (result.source !== requestedUrl) return { status: 'loading', url: '' };
  return { status: result.status, url: result.url };
}
